import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Filter, MoreVertical, Mail, User, X, CreditCard, Calendar, Ticket, DollarSign, ChevronLeft, Phone, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { mockCustomers } from '@/lib/mockData';
import { formatCurrency, formatDate } from '@/lib/utils';

// Dropdown Menu for customer actions
const CustomerMenu = ({ customer, onViewProfile }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button 
        className="p-2 hover:bg-secondary rounded-lg"
        onClick={() => setIsOpen(!isOpen)}
        data-testid={`customer-menu-${customer.id}`}
      >
        <MoreVertical className="w-4 h-4 text-muted-foreground" />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-1 w-40 bg-card border border-border rounded-lg shadow-xl overflow-hidden z-50">
            <button 
              className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-secondary transition-colors flex items-center gap-2"
              onClick={() => { onViewProfile(customer); setIsOpen(false); }}
              data-testid={`view-profile-${customer.id}`}
            >
              <User className="w-4 h-4" />
              View Profile
            </button>
            <button 
              className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-secondary transition-colors flex items-center gap-2"
              onClick={() => setIsOpen(false)}
            >
              <Mail className="w-4 h-4" />
              Send Email
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// Customer Profile Panel - Full page
const CustomerProfilePanel = ({ customer, isOpen, onClose }) => {
  if (!isOpen || !customer) return null;

  // Obfuscate card number - show only last 4 digits
  const obfuscateCardNumber = (cardNumber) => {
    if (!cardNumber) return '•••• •••• •••• ••••';
    const last4 = cardNumber.slice(-4);
    return `•••• •••• •••• ${last4}`;
  };

  // Generate mock payment method data
  const paymentMethod = customer.paymentMethod || {
    type: 'card',
    brand: 'Visa',
    last4: '4242',
    expiryMonth: '12',
    expiryYear: '2025'
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="customer-profile-panel">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      
      {/* Panel */}
      <div className="relative w-full max-w-lg bg-card border-l border-border h-full overflow-hidden flex flex-col animate-in slide-in-from-right">
        {/* Header */}
        <div className="bg-card border-b border-border p-4 flex items-center gap-3 flex-shrink-0">
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg" data-testid="close-profile-panel">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
          <h2 className="text-lg font-semibold text-white">Customer Profile</h2>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Profile Header */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
              <User className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">{customer.name}</h3>
              <p className="text-muted-foreground">{customer.email}</p>
            </div>
          </div>

          {/* Registration Info */}
          <Card className="bg-secondary/30 border-border p-4 space-y-3">
            <h4 className="text-sm font-semibold text-white flex items-center gap-2">
              <User className="w-4 h-4 text-primary" />
              Registration Details
            </h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-muted-foreground">Full Name</p>
                <p className="text-white">{customer.name}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Email</p>
                <p className="text-white">{customer.email}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Phone</p>
                <p className="text-white">{customer.phone || '+1 (555) 123-4567'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Location</p>
                <p className="text-white">{customer.location || 'New York, NY'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Member Since</p>
                <p className="text-white">{formatDate(customer.memberSince || '2024-01-15')}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Status</p>
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                  Active
                </span>
              </div>
            </div>
          </Card>

          {/* Payment Method - Obfuscated */}
          <Card className="bg-secondary/30 border-border p-4 space-y-3">
            <h4 className="text-sm font-semibold text-white flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-primary" />
              Payment Method
            </h4>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-card rounded-lg border border-border">
                <div className="w-10 h-7 bg-gradient-to-r from-blue-600 to-blue-800 rounded flex items-center justify-center">
                  <span className="text-white text-[10px] font-bold">{paymentMethod.brand?.toUpperCase() || 'VISA'}</span>
                </div>
                <div className="flex-1">
                  <p className="text-white font-mono text-sm">
                    {obfuscateCardNumber(paymentMethod.cardNumber || `************${paymentMethod.last4 || '4242'}`)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Expires {paymentMethod.expiryMonth || '12'}/{paymentMethod.expiryYear || '25'}
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Card details are obfuscated for security. Only the last 4 digits are displayed.
              </p>
            </div>
          </Card>

          {/* Purchase Stats */}
          <Card className="bg-secondary/30 border-border p-4 space-y-3">
            <h4 className="text-sm font-semibold text-white flex items-center gap-2">
              <Ticket className="w-4 h-4 text-primary" />
              Purchase History
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-card rounded-lg">
                <p className="text-2xl font-bold text-primary">{customer.ticketsPurchased}</p>
                <p className="text-xs text-muted-foreground">Tickets Purchased</p>
              </div>
              <div className="text-center p-3 bg-card rounded-lg">
                <p className="text-2xl font-bold text-accent">{formatCurrency(customer.totalSpent)}</p>
                <p className="text-xs text-muted-foreground">Total Spent</p>
              </div>
            </div>
            <div className="pt-2 border-t border-border">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Last Purchase</span>
                <span className="text-white">{formatDate(customer.lastPurchase)}</span>
              </div>
            </div>
          </Card>

          {/* Recent Events */}
          <Card className="bg-secondary/30 border-border p-4 space-y-3">
            <h4 className="text-sm font-semibold text-white flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary" />
              Recent Events Attended
            </h4>
            <div className="space-y-2">
              {(customer.recentEvents || [
                { name: 'No Sign of Weakness', date: '2024-12-15' },
                { name: 'Jazz Night Live', date: '2024-11-20' },
              ]).map((event, idx) => (
                <div key={idx} className="flex justify-between items-center p-2 bg-card rounded-lg">
                  <span className="text-white text-sm">{event.name}</span>
                  <span className="text-xs text-muted-foreground">{formatDate(event.date)}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Footer */}
        <div className="bg-card border-t border-border p-4 flex-shrink-0">
          <Button 
            variant="outline"
            className="w-full border-border"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export function AdminCustomerDirectory() {
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-xl lg:text-2xl xl:text-3xl font-bold text-white">Customer Directory</h1>
            <p className="text-muted-foreground mt-1 text-sm">{mockCustomers.length} total customers</p>
          </div>
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Mail className="w-4 h-4 mr-2" />
            Export List
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              placeholder="Search customers..." 
              className="bg-card border-border pl-10"
            />
          </div>
          <Button variant="outline" className="border-border">
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </Button>
        </div>

        {/* Customer Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 mb-6">
          <Card className="bg-card border-border p-4 lg:p-6">
            <h3 className="text-sm lg:text-base font-semibold text-white mb-2">Total Customers</h3>
            <p className="text-2xl lg:text-3xl font-bold text-primary">{mockCustomers.length}</p>
          </Card>
          <Card className="bg-card border-border p-4 lg:p-6">
            <h3 className="text-sm lg:text-base font-semibold text-white mb-2">Total Revenue</h3>
            <p className="text-2xl lg:text-3xl font-bold text-accent">
              {formatCurrency(mockCustomers.reduce((sum, c) => sum + c.totalSpent, 0))}
            </p>
          </Card>
          <Card className="bg-card border-border p-4 lg:p-6">
            <h3 className="text-sm lg:text-base font-semibold text-white mb-2">Avg. Spend</h3>
            <p className="text-2xl lg:text-3xl font-bold text-white">
              {formatCurrency(mockCustomers.reduce((sum, c) => sum + c.totalSpent, 0) / mockCustomers.length)}
            </p>
          </Card>
        </div>

        {/* Customers Table */}
        <Card className="bg-card border-border overflow-hidden">
          {/* Desktop Table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-secondary/30">
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Customer</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Email</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Tickets Purchased</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Total Spent</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Last Purchase</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {mockCustomers.map(customer => (
                  <tr key={customer.id} className="border-b border-border hover:bg-secondary/30 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <span className="font-medium text-white">{customer.name}</span>
                      </div>
                    </td>
                    <td className="p-4 text-muted-foreground">{customer.email}</td>
                    <td className="p-4 text-white">{customer.ticketsPurchased}</td>
                    <td className="p-4 text-primary font-semibold">{formatCurrency(customer.totalSpent)}</td>
                    <td className="p-4 text-muted-foreground">{formatDate(customer.lastPurchase)}</td>
                    <td className="p-4">
                      <CustomerMenu 
                        customer={customer} 
                        onViewProfile={setSelectedCustomer}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile List */}
          <div className="lg:hidden divide-y divide-border">
            {mockCustomers.map(customer => (
              <div key={customer.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-medium text-white">{customer.name}</h3>
                      <p className="text-xs text-muted-foreground">{customer.email}</p>
                    </div>
                  </div>
                  <CustomerMenu 
                    customer={customer} 
                    onViewProfile={setSelectedCustomer}
                  />
                </div>
                <div className="flex items-center gap-4 mt-3 text-xs">
                  <span className="text-muted-foreground">Tickets: <span className="text-white">{customer.ticketsPurchased}</span></span>
                  <span className="text-muted-foreground">Spent: <span className="text-primary font-medium">{formatCurrency(customer.totalSpent)}</span></span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Customer Profile Panel */}
      <CustomerProfilePanel
        customer={selectedCustomer}
        isOpen={!!selectedCustomer}
        onClose={() => setSelectedCustomer(null)}
      />
    </AdminLayout>
  );
}

export default AdminCustomerDirectory;
